import { GQLProductCollectionFragment, GQLProductTileFragment, GQLProductTileWithColourGroupsFragment } from '@theprintbar/graphql';
import {
	ImageSize,
	WebRoutes,
	cn,
	getFlatLaysSortedBySide,
	getProductImageUrl,
	getSamedayName,
	getSortedSides,
	withDomain,
} from '@theprintbar/shared';
import { ImageContainer, LightningBolt, Pill } from '@theprintbar/ui';
import { ReactNode, useEffect, useRef } from 'react';

export interface ProductTileImageProps {
	product: GQLProductTileFragment | GQLProductTileWithColourGroupsFragment;
	webDomain?: string;
	productPath?: string;
	collectionList?: GQLProductCollectionFragment[];
	activeColour?: string;
	hideButtons?: boolean;
	hideSameday?: boolean;
	sameDayText?: string;
	customPill?: { text: string; colour: string; icon: ReactNode };
}

export function ProductTileImage({
	product,
	webDomain,
	collectionList,
	activeColour,
	hideSameday,
	sameDayText,
	customPill,
}: ProductTileImageProps) {
	const { collections, minSize, colours, maxSize } = product;
	const flatLayRef = useRef<HTMLImageElement>(null);
	const showSameday = !hideSameday && product.isSameday && product.inStock;
	const samedayName = getSamedayName();
	const sameDayTextDisplay = sameDayText ?? (
		<>
			{samedayName} <span className="hidden md:block ml-1">Available</span>
		</>
	);

	useEffect(() => {
		if (flatLayRef.current && activeColour) {
			const colour = colours.find(({ colour }) => colour.code === activeColour);
			if (!colour) return;

			flatLayRef.current.src = getProductImageUrl({
				product,
				productCode: product.code,
				colourCode: colour.colour.code,
				size: ImageSize.Large,
				webDomain,
			});
		}
	}, [activeColour, colours]);

	return (
		<div className="relative">
			{!product.inStock && (
				<Pill
					className={cn(
						'absolute bg-gray-200 text-grey border-grey top-0 -translate-y-1/2 right-4 z-10 uppercase flex items-center font-belfast text-xs font-bold py-1 px-3',
					)}
				>
					OUT OF STOCK
				</Pill>
			)}
			{(showSameday || customPill) && (
				<Pill
					className={cn(
						'absolute top-0 -translate-y-1/2 right-4 z-10 uppercase flex items-center font-belfast text-xs font-bold py-1 px-3',
						customPill?.colour || ' bg-blue-bright',
					)}
				>
					{customPill?.text || sameDayTextDisplay}
					{customPill?.icon || <LightningBolt className="w-3.5 h-3.5 -mt-px -mr-1" />}
				</Pill>
			)}

			<div
				className={cn(
					'relative overflow-hidden shadow-lg rounded-3xl aspect-[3/4] bg-white-mid flex flex-col justify-center border border-blue',
					product.inStock ? 'bg-white-mid' : 'bg-gray-50',
				)}
			>
				<div className="absolute inset-0">
					{!product.inStock && <div className="absolute inset-0 z-1 bg-grey opacity-10" />}
					<div className={cn('absolute w-full h-full', !activeColour && 'opacity-0')}>
						<img
							ref={flatLayRef}
							alt={`${product.name} Flat image`}
							className={cn('absolute h-full w-full object-cover object-center')}
						/>
					</div>
					<div className={cn('absolute inset-0 transition-opacity ease-in-out', !!activeColour && 'opacity-0')}>
						<img
							src={getProductImageUrl({
								product,
								productCode: product.code,
								size: ImageSize.Large,
								webDomain,
							})}
							alt={`${product.name} Model image`}
							className={cn(
								'group-hover:scale-105 transition-all ease-in-out duration-300 absolute h-full w-full object-cover object-center',
							)}
						/>
					</div>

					{/*Collection of tags */}
					<div className="absolute top-3 left-3 flex flex-col space-y-2">
						{collections?.map(({ id }) => {
							const collection = collectionList?.find(({ id: collectionId }) => collectionId === id);
							if (!collection) return null;

							return (
								<div
									key={collection.id}
									className="p-1.5 rounded-full border border-blue flex justify-center items-center"
									style={{ backgroundColor: collection.colour || '' }}
								>
									<ImageContainer size={ImageSize.Tiny} image={collection.icon} className="h-4 w-4" />
								</div>
							);
						})}
					</div>

					{!!minSize && !!maxSize && (
						<Pill className="border-none bg-gray-200 text-grey absolute bottom-5 right-4 h-6 font-belfast font-bold text-xs uppercase opacity-100">
							{minSize.code === 'OS' ? 'One Size' : minSize.code} {maxSize.code !== minSize.code ? `TO ${maxSize.code}` : ''}
						</Pill>
					)}
				</div>
			</div>
		</div>
	);
}
